import styled from '@emotion/styled'
import {
    FormControl,
    FormControlLabel,
    Popover,
    Radio,
    RadioGroup,
    Select,
} from '@mui/material'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { ListType, SettingsType } from '../../../../../constants/enums'
import { cam2camSettingsDictionary } from '../../../../../mfcLogic/settings'
import { slugToID } from '../../../../../state/state.users'
import i18n from '../../../../../utilities/i18n'
import ButtonChange from '../../../../Buttons/ButtonChange'
import { useList } from '../../../../Hooks/useList'
import { useSettingSelector } from '../../../../Hooks/useSettings'
import BadgeAvatar from '../../../../common/BadgeAvatar'
import { StyledMenuItem } from '../../../../common/MenuItem'
import { getThemeSpacing } from '../../../../styles/appTheme'
import APP_BREAKPOINTS from '../../../../styles/breakpoints'
import { palette } from '../../../../styles/colors'
import { APP_FONT } from '../../../../theme'
import Tooltip, {
    defaultAnchorOrigin,
    defaultTransformOrigin,
} from '../../../Tooltip'

export const HEIGHT_WHO_CAN_WATCH = 90

const StyledRoot = styled('div')({
    fontColor: palette.standardText,
    padding: getThemeSpacing(1),
    display: 'flex',
    flexFlow: 'column nowrap',
    height: HEIGHT_WHO_CAN_WATCH,
    minHeight: HEIGHT_WHO_CAN_WATCH,
    maxHeight: HEIGHT_WHO_CAN_WATCH,
    marginBottom: getThemeSpacing(1),
})
const StyledColumn = styled('div')({
    display: 'flex',
    flexFlow: 'column nowrap',
})
const StyledRow = styled('div')({
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `0 ${getThemeSpacing(1)}`,
    width: '100%',
    fontWeight: 600,
})
const StyledTitle = styled(StyledRow)({
    justifyContent: 'start',
    paddingBottom: getThemeSpacing(1),
    fontWeight: 600,
})
const StyledSelectWrapper = styled('div')({
    paddingLeft: getThemeSpacing(2.6),
})
const StyledSelect = styled(Select)({
    fontFamily: APP_FONT,
})
const StyledOptions = styled('div')({
    padding: getThemeSpacing(2),
})
const StyledFormControlLabel = styled(FormControlLabel)({
    '.MuiFormControlLabel-label': {
        fontSize: '0.8rem',
        fontColor: palette.standardText,
    },
})
const StyledTooltipWrapper = styled('div')({
    padding: getThemeSpacing(2),
    [`@media (max-width: ${APP_BREAKPOINTS.s}px)`]: {
        maxWidth: 320,
    },
})

const styleSelect = {
    maxHeight: 80,
    minWidth: 100,
    width: '80%',
} as const

const popoverAnchorOrigin = {
    vertical: 'top',
    horizontal: 'center',
} as const
const popoverTransformOrigin = {
    vertical: 'bottom',
    horizontal: 'center',
} as const

const FormOptions = Object.keys(cam2camSettingsDictionary).map((key) =>
    key ? (
        <StyledFormControlLabel
            key={key}
            control={<Radio color="primary" />}
            value={key}
            label={i18n.t(`userBroadcast.label_${key}`)}
            data-testid={`UserBroadcast__${key}`}
        />
    ) : null,
)

const MenuItemComponent = memo(({ value }: { value: string }) => (
    <StyledMenuItem>{value}</StyledMenuItem>
))

interface IWhoCanWatch {
    userId: number
    updateBroadcastDisabledState(disabled: boolean): void
}

const WhoCanWatch = (props: IWhoCanWatch) => {
    const { userId, updateBroadcastDisabledState } = props
    const friendsList = useList(ListType.PREMIUM_OR_MODEL_FRIENDS, {
        returnIDs: false,
    }).filter(({ id }) => id !== userId)

    const [ueoptValue, saveWebcamOptionsSettings] = useSettingSelector(
        SettingsType.MY_WEBCAM,
        'options',
    )

    const [showPopper, setShowPopper] = useState(false)
    const [whoCanWatchValue, setWhoCanWatchValue] = useState('onlyModels')

    const [friendName, setFriendName] = useState('')

    const rootRef = useRef(null)
    const whoCanWatchRef = useRef(null)
    const friendsListLoaded = useRef(false)

    const onClickButtonChange = useCallback(
        () => setShowPopper(!showPopper),
        [showPopper],
    )

    const closeWhoCanWatch = useCallback(() => setShowPopper(false), [])

    const dispatchWhoCanWatch = useCallback((param: number) => {
        const value = param !== 3 ? param : 0
        saveWebcamOptionsSettings(value)
    }, [])

    const onSelectSpecificUser = useCallback(({ target: { value } }) => {
        setShowPopper(false)
        setFriendName(value)
        dispatchWhoCanWatch(slugToID[value.toLowerCase()])
    }, [])

    /** Fire off updated UEOPT request to FCS when changed */
    const onChangeRadio = useCallback(({ target: { value } }) => {
        const param = cam2camSettingsDictionary[value]
        setShowPopper(value === 'onlySpecifiedFriend')
        setWhoCanWatchValue(value)
        if (param < 100) {
            setFriendName('')
            dispatchWhoCanWatch(param)
        }
    }, [])

    const selectRenderValue = useCallback(
        (value?: any) => <MenuItemComponent value={value || friendName} />,
        [friendName],
    )

    const getWhoCanWatchValue = () => {
        if (ueoptValue > 100) {
            const friends = friendsList.filter(
                (friend) => friend.id === ueoptValue,
            )
            if (friends.length) {
                setFriendName(friends[0].name)
            }
            return 'onlySpecifiedFriend'
        } else {
            return (
                Object.keys(cam2camSettingsDictionary).filter(
                    (key) => cam2camSettingsDictionary[key] === ueoptValue,
                )[0] || 'onlyModels'
            )
        }
    }

    useEffect(() => {
        if (whoCanWatchValue === 'onlySpecifiedFriend' && !friendName) {
            updateBroadcastDisabledState(true)
        } else {
            updateBroadcastDisabledState(false)
        }
    }, [])

    useEffect(() => {
        setWhoCanWatchValue(getWhoCanWatchValue)
    }, [ueoptValue])

    useEffect(() => {
        if (!friendsList || !friendsList.length || friendsListLoaded.current) {
            return
        }
        setWhoCanWatchValue(getWhoCanWatchValue)
        friendsListLoaded.current = true
    }, [friendsList])

    return (
        <StyledRoot ref={rootRef}>
            <StyledColumn>
                <StyledTitle>
                    {i18n.t('userBroadcast.whoCanWatchYourWebcam')}
                    <Tooltip
                        anchorOrigin={defaultAnchorOrigin}
                        transformOrigin={defaultTransformOrigin}
                    >
                        <StyledTooltipWrapper>
                            {i18n.t('userBroadcast.tooltipBroadcastWillOnly')}.
                        </StyledTooltipWrapper>
                    </Tooltip>
                </StyledTitle>
                <StyledRow>
                    <div ref={whoCanWatchRef}>
                        {friendName !== '' ? (
                            <span data-testid="Label__SelectedFriend">
                                {friendName}
                            </span>
                        ) : (
                            i18n.t(`userBroadcast.label_${whoCanWatchValue}`)
                        )}
                    </div>
                    <ButtonChange onClick={onClickButtonChange} />
                </StyledRow>
            </StyledColumn>
            <Popover
                anchorEl={whoCanWatchRef.current}
                open={showPopper}
                onClose={closeWhoCanWatch}
                anchorOrigin={popoverAnchorOrigin}
                transformOrigin={popoverTransformOrigin}
            >
                <StyledOptions>
                    <FormControl component="fieldset" variant="filled">
                        <RadioGroup
                            name="whoCanWatchValue"
                            onChange={onChangeRadio}
                            value={whoCanWatchValue}
                        >
                            {FormOptions}
                        </RadioGroup>
                    </FormControl>
                    <StyledSelectWrapper
                        hidden={whoCanWatchValue !== 'onlySpecifiedFriend'}
                    >
                        <StyledSelect
                            data-testid="UserBroadcast__SelectFriend"
                            MenuProps={{
                                anchorEl: whoCanWatchRef.current,
                                anchorReference: 'anchorEl',
                                marginThreshold: 90,
                            }}
                            onChange={onSelectSpecificUser}
                            renderValue={selectRenderValue}
                            value={friendName}
                            variant="standard"
                            style={styleSelect}
                        >
                            {friendsList.map((friend) => (
                                <StyledMenuItem
                                    data-testid="Option__Friend"
                                    key={friend.id}
                                    value={friend.name}
                                >
                                    <BadgeAvatar
                                        size={32}
                                        alt={friend.name}
                                        id={friend.id}
                                        disableBadge={true}
                                    />{' '}
                                    <span
                                        css={{
                                            paddingLeft: getThemeSpacing(0.5),
                                        }}
                                    >
                                        <strong>{friend.name}</strong>
                                    </span>
                                </StyledMenuItem>
                            ))}
                        </StyledSelect>
                    </StyledSelectWrapper>
                </StyledOptions>
            </Popover>
        </StyledRoot>
    )
}

export default memo(WhoCanWatch)
